import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useNavigate } from 'react-router-dom';

interface ContractValueData {
  serviceType: string;
  totalValue: number;
  count: number;
  averageValue: number;
}

interface ContractValueDistributionProps {
  data: ContractValueData[];
}

// Standardized color mapping for utility types
const UTILITY_TYPE_COLORS: Record<string, string> = {
  'electricity': '#10B981', // Green
  'gas': '#F59E0B', // Orange
  'water': '#3B82F6', // Blue
  'telecoms': '#8B5CF6', // Purple
  'waste': '#EF4444', // Red
  'ev': '#06B6D4', // Cyan
  'renewable': '#84CC16', // Lime
  'renewables': '#84CC16', // Lime
  'insurance': '#EC4899', // Pink
  'merchant services': '#F97316', // Orange-red
  'new connection': '#14B8A6', // Teal
  'ppa export contract': '#06B6D4', // Cyan
  'default': '#64748B' // Slate gray for unknown types
};

const ContractValueDistribution: React.FC<ContractValueDistributionProps> = ({ data }) => {
  const navigate = useNavigate();

  // Process data for pie chart
  const processedData = data
    .filter(item => item.totalValue > 0)
    .map((item, index) => {
      const normalizedType = item.serviceType.toLowerCase();
      const displayLabel = item.serviceType
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      return {
        id: `${item.serviceType}-${index}`,
        value: item.totalValue,
        label: displayLabel,
        color: UTILITY_TYPE_COLORS[normalizedType] || UTILITY_TYPE_COLORS.default,
        count: item.count,
        averageValue: item.averageValue,
        serviceType: item.serviceType
      };
    })
    .sort((a, b) => b.value - a.value); // Sort by total value descending

  // Handle click on pie chart segment or legend
  const handleClick = (serviceType: string) => {
    console.log('ContractValueDistribution click:', serviceType);
    navigate(`/utilities/contracts?serviceType=${encodeURIComponent(serviceType)}`);
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <Card sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(4px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 2,
      color: 'white'
    }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
          Contract Value Distribution
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
          Click on a service type to view contracts
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', height: 300 }}>
          <Box sx={{ flex: 1, height: '100%' }}>
            <PieChart
              series={[
                {
                  data: processedData,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 30, additionalRadius: -30 },
                },
              ]}
              height={300}
              margin={{ right: 20 }}
              legend={{ hidden: true }}
              tooltip={{
                trigger: 'item',
              }}
            />
          </Box>
          <Box sx={{ 
            width: 180, 
            ml: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 1,
            maxHeight: 300,
            overflowY: 'auto'
          }}>
            {processedData.map((item) => (
              <Box
                key={item.id}
                onClick={() => handleClick(item.serviceType)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  padding: '6px 8px',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      backgroundColor: item.color,
                      borderRadius: '2px',
                      mr: 1,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ 
                    fontSize: 12, 
                    color: 'white',
                    lineHeight: 1.2,
                    fontWeight: 500
                  }}>
                    {item.label}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ 
                  fontSize: 11, 
                  color: 'rgba(255, 255, 255, 0.7)',
                  ml: 3,
                  lineHeight: 1.2
                }}>
                  {formatCurrency(item.value)}
                </Typography>
                <Typography variant="caption" sx={{ 
                  fontSize: 10, 
                  color: 'rgba(255, 255, 255, 0.5)',
                  ml: 3,
                  lineHeight: 1.2
                }}>
                  {item.count} contract{item.count !== 1 ? 's' : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export { ContractValueDistribution };
